import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { completeSignIn, getAuthClient, hydrateSession, signOutCustomer, useAuthSnapshot } from "~/widgets/lib/auth-client";
import { authStore } from "~/widgets/stores/auth-store";
import type { ViewContext } from "../types";
import { sendJson } from "../api";
import { Notice } from "../parts";
import { CustomerAuthDialog } from "../../CustomerAuthDialog/CustomerAuthDialog";
import { SWITCH_FAILED, fallbackCopy, grantStep, verifiedToken, type BundleFallback, type BundleGrantStep } from "./bundle-auth/bundleAuth";

// /auth/bundle/<bundle-id>?sig=…: the link in a bundle's emails. Port of
// v1's AuthBundleView: once the stored session has settled, the signed
// link is checked (POST /widget/bundle-email-auth); its one-off grant is
// redeemed through the same customer auth client <baluu-profile> and the
// booking pages use, so the session lands where they read it; then the
// bundle's page opens. The answer names no school: the bundle page refuses
// another school's bundle after the redirect.

type State = { kind: "loading" } | Extract<BundleGrantStep, { kind: "switch" }> | ({ kind: "fallback" } & BundleFallback);

/** Leave for `url` without keeping this page in history (the link is
 * spent). Outside the component: the compiler forbids it inside. */
function leaveFor(url: string) {
  window.location.replace(url);
}

/** Redeem a grant as the current customer: true once signed in. */
async function redeem(api: string, slug: string, grantToken: string): Promise<boolean> {
  const client = getAuthClient(api, slug);
  const before = authStore.getToken();
  const { data, error } = await client.$fetch<{ token?: string }>("/magic-link/verify", { method: "GET", query: { token: grantToken } });
  if (error) return false;
  // The client's onSuccess stores the set-auth-token header; when the
  // header didn't come through, the answer's own token stands in.
  const token = verifiedToken(data);
  if (authStore.getToken() === before && token) authStore.setToken(token);
  return !!(await completeSignIn(api, slug));
}

export function BundleAuthView({ ctx, bundleId }: { ctx: ViewContext; bundleId: string }) {
  const auth = useAuthSnapshot();
  const settled = auth.status !== "unknown" || !auth.token;
  const [sig] = useState(() => new URLSearchParams(window.location.search).get("sig") ?? "");
  const [state, setState] = useState<State>({ kind: "loading" });
  const ran = useRef(false);
  const slug = ctx.businessSlug;
  const bundleHref = ctx.links.bundle(bundleId);

  useEffect(() => {
    if (!settled) {
      void hydrateSession(ctx.api, slug);
      return;
    }
    if (ran.current) return;
    ran.current = true;
    const currentEmail = auth.status === "authenticated" ? auth.user?.email?.toLowerCase() || null : null;
    void (async () => {
      try {
        const answer = await sendJson<unknown>(`${ctx.api}/widget/bundle-email-auth`, "POST", { bundleId, sig, currentEmail });
        const step = grantStep(answer, currentEmail);
        if (step.kind === "open") return leaveFor(bundleHref);
        if (step.kind === "verify") {
          if (await redeem(ctx.api, slug, step.grantToken)) return leaveFor(bundleHref);
          return setState({ kind: "fallback", reason: "invalid_link" });
        }
        setState(step);
      } catch {
        setState({ kind: "fallback", reason: "invalid_link" });
      }
    })();
  }, [settled, auth.status, auth.user, ctx.api, slug, bundleId, sig, bundleHref]);

  if (state.kind === "switch") return <ConfirmSwitch ctx={ctx} step={state} onDone={() => leaveFor(bundleHref)} />;
  if (state.kind === "fallback") return <Fallback ctx={ctx} fallback={state} bundleHref={bundleHref} />;
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <p className="text-sm text-muted-foreground">Signing you in…</p>
    </div>
  );
}

/** Signed in as someone else: switch to the bundle's owner, or stay. In
 * place of v1's dialog (no portals in the shadow root). */
function ConfirmSwitch({ ctx, step, onDone }: { ctx: ViewContext; step: Extract<BundleGrantStep, { kind: "switch" }>; onDone: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function confirm() {
    setSubmitting(true);
    setError("");
    try {
      await signOutCustomer(ctx.api, ctx.businessSlug);
      if (await redeem(ctx.api, ctx.businessSlug, step.grantToken)) return onDone();
      setError(SWITCH_FAILED);
    } catch {
      setError(SWITCH_FAILED);
    }
    setSubmitting(false);
  }

  return (
    <div className="mx-auto max-w-md space-y-4 rounded-2xl border bg-card p-8 shadow-sm">
      <h1 className="text-xl font-semibold">Switch accounts to manage {step.displayName}?</h1>
      <p className="text-sm text-muted-foreground">
        This {step.displayName} belongs to <strong>{step.ownerEmail}</strong>. You’re currently signed in as <strong>{step.currentEmail}</strong>.
      </p>
      {error && <Notice tone="error">{error}</Notice>}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button asChild variant="outline" disabled={submitting}>
          <a href={ctx.links.home()} aria-disabled={submitting}>
            Stay signed in as {step.currentEmail}
          </a>
        </Button>
        <Button onClick={() => void confirm()} disabled={submitting}>
          {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
          {submitting ? "Switching…" : `Continue as ${step.ownerEmail}`}
        </Button>
      </div>
    </div>
  );
}

function Fallback({ ctx, fallback, bundleHref }: { ctx: ViewContext; fallback: BundleFallback; bundleHref: string }) {
  const [signInOpen, setSignInOpen] = useState(false);
  const copy = fallbackCopy(fallback);
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="max-w-md rounded-2xl border bg-card p-8 text-center shadow-sm">
        <h1 className="mb-2 text-xl font-semibold">{copy.headline}</h1>
        <p className="mb-6 text-sm text-muted-foreground">{copy.message}</p>
        <Button onClick={() => setSignInOpen(true)}>Sign in with email</Button>
      </div>
      <CustomerAuthDialog
        open={signInOpen}
        onOpenChange={setSignInOpen}
        apiUrl={ctx.api}
        businessSlug={ctx.businessSlug}
        primaryColor={ctx.primaryColor}
        onSignedIn={() => leaveFor(copy.after === "bundle" ? bundleHref : ctx.links.profile())}
      />
    </div>
  );
}
